import React, { FC } from 'react';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { ActionPrice, MaterialPriceDifference } from 'models/organization';
import { useTranslation } from 'react-i18next';
import ActionBasedClinicActionItem from './action-based-clinic-action-item.component';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { CustomerSetting } from 'models/customer-setting';
import { OrderBy } from 'models/search-result';
import SortableHeaderLabel from 'components/shared/sortable-header-label/sortable-header-label.component';
import { ACTION_BASED_SORT_FIELDS } from 'utils/price-list-sort';

type ActionBasedClinicActionListProps = {
    clientCurrency: string;
    clientId: string;
    /** DD-3697: the chain whose prices these are, for the per-row price history. */
    chainId: string;
    clientCustomerSettings: CustomerSetting[];
    actions: ActionPrice[];
    showAllPriceSupplement: boolean;
    materialGroups: MaterialPriceDifference[];
    materialGroupsLoading: boolean;
    onDisplayShowAllButton: (arg: boolean) => void;
    refetchActionList: () => void;
    refetchMaterialGroups: () => void;
    orderBy: OrderBy;
    onSortChange: (field: string) => void;
};
const ActionBasedClinicActionList: FC<ActionBasedClinicActionListProps> = ({
    clientCurrency,
    clientId,
    chainId,
    clientCustomerSettings,
    actions,
    showAllPriceSupplement,
    materialGroups,
    materialGroupsLoading,
    onDisplayShowAllButton,
    refetchActionList,
    refetchMaterialGroups,
    orderBy,
    onSortChange,
}: ActionBasedClinicActionListProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'orders', 'actions-materials']);

    const canEditActionList = customerSettings.getCustomerSetting(
        clientCustomerSettings,
        CONSTANTS.SETTINGS_NAMES.EDIT_ACTION_PRICE_LIST,
    );

    const isTioTandvardPricingEnabled = customerSettings.getCustomerSetting(
        clientCustomerSettings,
        CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING,
    );

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell></TableCell>
                        <TableCell>
                            <SortableHeaderLabel
                                field={ACTION_BASED_SORT_FIELDS.MAIN_CATEGORY}
                                label={translate('client-directory:MAIN_CATEGORY')}
                                orderBy={orderBy}
                                onSortChange={onSortChange}
                            />
                        </TableCell>
                        <TableCell>
                            <SortableHeaderLabel
                                field={ACTION_BASED_SORT_FIELDS.SUB_CATEGORY}
                                label={translate('client-directory:SUB_CATEGORY')}
                                orderBy={orderBy}
                                onSortChange={onSortChange}
                            />
                        </TableCell>
                        <TableCell>
                            <SortableHeaderLabel
                                field={ACTION_BASED_SORT_FIELDS.NUMBER}
                                label={translate('actions-materials:CODE')}
                                orderBy={orderBy}
                                onSortChange={onSortChange}
                            />
                        </TableCell>
                        <TableCell>{translate('orders:ACTION')}</TableCell>
                        <TableCell>{translate('common:PRICE')}</TableCell>
                        {isTioTandvardPricingEnabled && <TableCell>{translate('common:TIOTANDVARD_PRICE')}</TableCell>}
                        <TableCell>{translate('client-directory:DELIVERY_TIME')}</TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        {canEditActionList && <TableCell></TableCell>}
                    </TableRow>
                </TableHead>
                {actions && !materialGroupsLoading ? (
                    <TableBody>
                        {actions.map((action) => {
                            return (
                                <ActionBasedClinicActionItem
                                    // Keyed by action, not position: rows hold edit state and
                                    // must keep it when the list is re-sorted.
                                    key={action._id}
                                    clientCurrency={clientCurrency}
                                    clientId={clientId}
                                    chainId={chainId}
                                    action={action}
                                    materialGroups={materialGroups}
                                    showAllPriceSupplement={showAllPriceSupplement}
                                    canEditActionList={canEditActionList}
                                    isTioTandvardPricingEnabled={isTioTandvardPricingEnabled}
                                    onDisplayShowAllButton={onDisplayShowAllButton}
                                    refetchActionList={refetchActionList}
                                    refetchMaterialGroups={refetchMaterialGroups}
                                />
                            );
                        })}
                    </TableBody>
                ) : undefined}
            </Table>
        </TableContainer>
    );
};

export default ActionBasedClinicActionList;
